import React, { useState, useEffect } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, ShieldAlert, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function BlockedWords() {
  const [custom, setCustom] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newWord, setNewWord] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [tab, setTab] = useState('custom');
  const [search, setSearch] = useState('');

  const fetchWords = () => {
    setLoading(true);
    api.get('/admin/blocked-words')
      .then(r => { setCustom(r.data.custom); setDefaults(r.data.defaults); })
      .catch(() => toast.error('Failed to load blocked words'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchWords(); }, []);

  const handleAdd = async () => {
    const word = newWord.trim().toLowerCase();
    if (!word) return;
    setAdding(true);
    try {
      const r = await api.post('/admin/blocked-words', { word });
      setCustom(prev => [...prev, r.data].sort((a, b) => a.word.localeCompare(b.word)));
      setNewWord('');
      toast.success(`"${word}" added to blocked list`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add word');
    } finally {
      setAdding(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/blocked-words/${deleteId}`);
      setCustom(prev => prev.filter(w => w.id !== deleteId));
      setDeleteId(null);
      toast.success('Word removed');
    } catch {
      toast.error('Failed to remove word');
    } finally {
      setDeleting(false);
    }
  };

  const displayList = tab === 'custom' ? custom : defaults;
  const filtered = displayList.filter(w => {
    const word = typeof w === 'string' ? w : w.word;
    return !search || word.includes(search.toLowerCase());
  });

  return (
    <DashboardShell title="Blocked Words">
      <div className="space-y-6 max-w-2xl">
        {/* Info */}
        <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(28,28,28,0.08)', border: '1px solid rgba(28,28,28,0.2)' }}>
          <ShieldAlert size={18} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--gold)' }} />
          <div className="text-sm" style={{ color: 'var(--text)' }}>
            <p className="font-medium">Word filter protects your community</p>
            <p className="mt-0.5 text-xs" style={{ color: 'var(--text-muted)' }}>Messages containing any blocked word will be rejected in real-time. You can add custom words on top of the built-in defaults.</p>
          </div>
        </div>

        {/* Add new word */}
        <div>
          <label className="block text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>Add custom blocked word</label>
          <div className="flex gap-2">
            <Input
              value={newWord}
              onChange={e => setNewWord(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder="Enter word to block…"
              className="text-sm"
              style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}
              data-testid="blocked-word-input"
            />
            <Button
              onClick={handleAdd}
              disabled={adding || !newWord.trim()}
              style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px', flexShrink: 0 }}
              data-testid="add-blocked-word-button"
            >
              <Plus size={14} className="mr-1" /> Add
            </Button>
          </div>
        </div>

        {/* Tabs + search */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList style={{ backgroundColor: 'var(--bg-subtle)' }}>
              <TabsTrigger value="custom">Custom ({custom.length})</TabsTrigger>
              <TabsTrigger value="defaults">Built-in defaults ({defaults.length})</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search words…"
              className="pl-8 text-xs w-48"
              style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}
            />
          </div>
        </div>

        {/* Word list */}
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
          {loading ? (
            <div className="p-4 space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-9 w-full" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {tab === 'custom' ? 'No custom words added yet' : 'No matching words'}
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 p-4">
              {filtered.map((w, i) => {
                const word = typeof w === 'string' ? w : w.word;
                const id = typeof w === 'object' ? w.id : null;
                return (
                  <div
                    key={id || word}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs"
                    style={{
                      backgroundColor: tab === 'custom' ? 'var(--bg-subtle)' : 'var(--bg)',
                      border: '1px solid var(--border-clr)',
                      color: 'var(--text)',
                    }}
                  >
                    <span>{word}</span>
                    {tab === 'custom' && id && (
                      <button
                        onClick={() => setDeleteId(id)}
                        className="ml-0.5 rounded-full p-0.5 hover:bg-red-50 transition-colors"
                        style={{ color: 'var(--danger)' }}
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Total blocked: {custom.length + defaults.length} words (custom + built-in)
        </p>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Remove word?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'var(--text-muted)' }}>This word will no longer be filtered from messages.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderRadius: '10px' }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} style={{ backgroundColor: 'var(--danger)', color: 'white', borderRadius: '10px' }}>
              {deleting ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}
